import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui';
import { authFetch } from '../../lib/api/authFetch';
import { API_BASE } from '../../lib/api/version';
import { checkGitHubAppStatus } from '../../lib/api/github';
import { StepByok } from './StepByok';

interface Props {
  projectId: string | null;
  initialOrg?: string;
  onDone: (repoFullName: string | null) => void;
  onSkip: () => void;
  onBack: () => void;
  /** UX-08: fired the first time the embedded API-keys disclosure is opened. */
  onByokOpen: () => void;
  /** UX-08: fired when the embedded API key save attempt completes (telemetry only). */
  onByokSaved: () => void;
}

type InstallStatus = 'idle' | 'checking' | 'installed' | 'not_installed' | 'error';

export const StepGithub: React.FC<Props> = ({ projectId, initialOrg, onDone, onSkip, onBack, onByokOpen, onByokSaved }) => {
  const [org, setOrg] = useState(initialOrg ?? '');
  const [repoName, setRepoName] = useState('');
  const [status, setStatus] = useState<InstallStatus>('idle');
  const [installUrl, setInstallUrl] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [attaching, setAttaching] = useState(false);
  const [attached, setAttached] = useState(false);

  const checkStatus = useCallback(async () => {
    if (!org.trim()) return;
    setStatus('checking');
    setErrorMsg('');
    try {
      const result = await checkGitHubAppStatus(org.trim());
      if (result.installed) {
        setStatus('installed');
      } else {
        setStatus('not_installed');
        setInstallUrl(result.installUrl ?? '');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Could not check GitHub App — you can continue and install later');
    }
  }, [org]);

  // Auto re-check on window focus (user installed the App in a new tab)
  useEffect(() => {
    if (status !== 'not_installed') return;
    const handler = () => { void checkStatus(); };
    window.addEventListener('visibilitychange', handler);
    return () => window.removeEventListener('visibilitychange', handler);
  }, [status, checkStatus]);

  const attachRepo = async () => {
    if (!projectId || !org.trim() || !repoName.trim()) return;
    const fullName = `${org.trim()}/${repoName.trim()}`;
    setAttaching(true);
    try {
      const r = await authFetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/repos`, {
        method: 'POST',
        body: JSON.stringify({ fullName }),
      });
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: { message?: string } };
        setErrorMsg(j?.error?.message ?? 'Failed to attach repo');
      } else {
        setAttached(true);
        onDone(fullName);
      }
    } catch (err) {
      setErrorMsg((err as Error).message);
    } finally {
      setAttaching(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <label className="block text-xs font-medium text-slate-700">GitHub organization</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={org}
            onChange={(e) => { setOrg(e.target.value); setStatus('idle'); }}
            placeholder="acme"
            className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm font-mono"
          />
          <Button variant="secondary" size="sm" onClick={checkStatus} loading={status === 'checking'} disabled={!org.trim()}>
            Check
          </Button>
        </div>

        {status === 'installed' && (
          <p className="text-xs text-success-700 bg-success-50 border border-success-200 rounded px-3 py-1.5">✓ Forge App is installed on {org}</p>
        )}
        {status === 'not_installed' && (
          <div className="text-xs bg-warning-50 border border-warning-200 rounded px-3 py-2 space-y-1.5">
            <p className="text-warning-800 font-medium">Forge GitHub App not installed on {org}</p>
            <div className="flex gap-2">
              {installUrl && (
                <a href={installUrl} target="_blank" rel="noopener noreferrer"
                  className="text-primary-700 underline">Install the Forge GitHub App →</a>
              )}
              <button type="button" onClick={checkStatus} className="text-slate-600 underline">Re-check</button>
            </div>
          </div>
        )}
        {status === 'error' && <p className="text-xs text-warning-700">{errorMsg}</p>}
      </div>

      {status === 'installed' && (
        <div className="space-y-2">
          <label className="block text-xs font-medium text-slate-700">First repo to connect</label>
          <div className="flex gap-2 items-center">
            <span className="text-sm text-slate-500 font-mono">{org}/</span>
            <input
              type="text"
              value={repoName}
              onChange={(e) => setRepoName(e.target.value)}
              placeholder="my-app"
              className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm font-mono"
            />
          </div>
          {errorMsg && <p className="text-xs text-danger-700">{errorMsg}</p>}
          {attached && <p className="text-xs text-success-700">✓ Repo connected</p>}
          {!attached && (
            <Button variant="secondary" size="sm" onClick={attachRepo} loading={attaching} disabled={!repoName.trim()}>
              Connect repo
            </Button>
          )}
        </div>
      )}

      <details
        className="border-t border-slate-200 pt-3"
        data-testid="onboard-byok-details"
        onToggle={(e) => {
          if ((e.target as HTMLDetailsElement).open) onByokOpen();
        }}
      >
        <summary className="cursor-pointer text-xs font-medium text-slate-600 hover:text-slate-900">
          Add your own API keys (optional)
        </summary>
        <div className="mt-3">
          <StepByok projectId={projectId} embedded onSaved={onByokSaved} />
        </div>
      </details>

      <div className="flex gap-3 pt-2">
        <Button variant="ghost" size="md" onClick={onBack} className="flex-1">Back</Button>
        <Button variant="ghost" size="md" onClick={onSkip} className="flex-1">Skip for now</Button>
        {attached && (
          <Button variant="primary" size="md" onClick={() => onDone(`${org}/${repoName}`)} className="flex-1">Continue</Button>
        )}
      </div>
    </div>
  );
};
